import React, { useMemo, useState } from "react";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import { GenericTable } from "@shared/components/GenericTable";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@shared/ui/breadcrumb";
import {
  ArrowLeft,
  Home,
  Building2,
  Activity,
  CheckSquare,
  Package,
  Wrench,
  HelpCircle,
  Users,
  User,
  Eye,
  Download,
  Edit,
  Phone,
  MapPin,
  Clock,
  TrendingUp,
  AlertTriangle,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useJobCardDetail } from "../../../hooks"; // Your existing hooks file
import { wxUtils, getStatusColor } from "../../../../utils"; // Your existing utils file
import { fa } from "zod/dist/types/v4/locales";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface AssignedConsumerServiceOrderViewProps {
  itemsPerPage?: number;
  backButtonText?: string;
  backNavigationPath?: string;
}

const AssignedConsumerServiceOrderView: React.FC<
  AssignedConsumerServiceOrderViewProps
> = ({
  itemsPerPage = 10,
  backButtonText = "Back to Dispatcher",
  backNavigationPath = "/service-orders/dispatcher?page=1&tab=assigned",
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const currency = useCurrency()
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch job card data using your existing pattern
  const { data: jobCardResponse } = useJobCardDetail({
    id: id,
  });

  // Transform data using your utility function
  const transformedData = useMemo(() => {
    if (!jobCardResponse) return null;
    return wxUtils.transformJobCardDetail(jobCardResponse);
  }, [jobCardResponse]);

  // Extract transformed data
  const {
    serviceOrderData,
    consumerDetails,
    meterDetails,
    technicianDetails,
    jobOverview,
    tasksData,
    materialsData,
    readingsData,
    helpResourcesData,
  } = transformedData || {};

  // KPI data derived from service order data
  const kpiData = useMemo(() => {
    if (!serviceOrderData) {
      return {
        slaStatus: "N/A",
        slaDueDate: "N/A",
        timeRemaining: "N/A",
      };
    }

    return {
      slaStatus: serviceOrderData.slaStatus,
      slaDueDate: serviceOrderData.expectedCompletion,
      timeRemaining: serviceOrderData.remainingTime,
    };
  }, [serviceOrderData]);

  // Helper functions (must be before useMemo to avoid hooks order issues)
  const createPaginationConfig = (data: any[]) => ({
    currentPage,
    totalPages: Math.ceil((data?.length || 0) / itemsPerPage),
    totalItems: data?.length || 0,
    itemsPerPage,
    startItem: (currentPage - 1) * itemsPerPage + 1,
    endItem: Math.min(currentPage * itemsPerPage, data?.length || 0),
  });

  const getPaginatedData = (data: any[]) => {
    if (!data || data.length === 0) return [];
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

    const getAddressStringForConsumer = (
    addressMap: any,
    isDirect: boolean = false
  ): string => {
    if (isDirect) {
      return `${addressMap?.premise || ""}, ${addressMap?.area || ""}, ${
        addressMap?.state || ""
      }`;
    }
    return `${addressMap?.premise || ""}, ${
      addressMap?.area || ""
    }, ${addressMap?.state || ""}`;
  };

  // Consumer & Meter / Technician tab components
  const consumerMeterTabComponents = useMemo(
    () => ({
      "consumer-meter": {
        label: "Consumer & Meter Details",
        icon: <Building2 className="h-4 w-4" />,
        shortLabel: "Consumer",
        component: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Consumer Details Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="h-5 w-5 text-blue-600" />
                Consumer Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Consumer Name
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {consumerDetails?.consumerName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Account No
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-mono">
                    {consumerDetails?.accountNo || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <p className="text-base text-gray-900 mt-1 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-600" />
                    {consumerDetails?.phone || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {consumerDetails?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Address
                  </label>
                  <p className="text-base text-gray-900 mt-1 flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-red-600 mt-0.5" />
                    {getAddressStringForConsumer(consumerDetails) || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Meter Details Section */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Activity className="h-5 w-5 text-orange-600" />
                Meter Details
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Meter No
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-mono">
                    {meterDetails?.meterNo || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Device No
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-mono">
                    {meterDetails?.deviceNo || "N/A"}
                  </p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Physical Status
                  </label>
                  <Badge className="bg-green-100 text-green-800">
                    {meterDetails?.physicalStatus || "N/A"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Assignment Status
                  </label>
                  <Badge className="bg-blue-100 text-blue-800">
                    {meterDetails?.assignmentStatus || "N/A"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        ),
      },
      technician: {
        label: "Assigned Technician",
        icon: <User className="h-4 w-4" />,
        shortLabel: "Tech",
        component: (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              Assignee Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Technician Name
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-medium">
                    {technicianDetails?.technicianName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    FID
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-mono">
                    {technicianDetails?.fid || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Phone
                  </label>
                  <p className="text-base text-gray-900 mt-1 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-green-600" />
                    {technicianDetails?.phone || "N/A"}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Role
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {technicianDetails?.role || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Email
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {technicianDetails?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Specialization
                  </label>
                  <Badge className="bg-purple-100 text-purple-800">
                    {technicianDetails?.specialization || "N/A"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        ),
      },
    }),
    [consumerDetails, meterDetails, technicianDetails]
  );

  // Table configurations
  const tasksColumns = [
    {
      key: "taskName",
      header: "Task Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "tatMins",
      header: "TAT (Mins)",
      render: (value: number) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "downtimeMins",
      header: "Downtime (Mins)",
      render: (value: number) => (
        <span className={value > 0 ? "text-orange-600" : "text-gray-700"}>
          {value}
        </span>
      ),
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes"
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },
    },
  ];

  const materialsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "size",
      header: "Size",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "quantity",
      header: "Quantity",
      render: (value: number) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "cost",
      header: "Cost",
      render: (value: number) => (
        <span className="font-semibold text-green-600">
          {currency} {value?.toFixed(2) || "0.00"}
        </span>
      ),
    },
  ];

  const readingsColumns = [
    {
      key: "name",
      header: "Name",
      render: (value: string) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "unit",
      header: "Unit",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "range",
      header: "Range",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "photoRequired",
      header: "Photo Required",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) =>
          value === "Yes"
            ? "bg-green-100 text-green-800"
            : "bg-gray-100 text-gray-800",
      },
    },
  ];

  const helpResourcesColumns = [
    {
      key: "resourceName",
      header: "Resource Name",
      render: (value: string) => (
        <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
          {value}
        </span>
      ),
    },
    {
      key: "resourceType",
      header: "Type",
      type: "badge" as const,
      badgeConfig: {
        getClassName: (value: string) => {
          switch (value) {
            case "Document":
            case "instruction":
              return "bg-blue-100 text-blue-800";
            case "Video Link":
              return "bg-red-100 text-red-800";
            case "Checklist":
              return "bg-green-100 text-green-800";
            case "Hotline":
              return "bg-purple-100 text-purple-800";
            default:
              return "bg-gray-100 text-gray-800";
          }
        },
      },
    },
    {
      key: "details",
      header: "Details",
      render: (value: string) => <span className="text-gray-700">{value}</span>,
    },
  ];
  const jobTabComponents = useMemo(
    () => ({
      overview: {
        label: "Overview",
        icon: <Activity className="h-4 w-4" />,
        component: (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="text-sm font-medium text-gray-500">
                Description
              </label>
              <p className="text-base text-gray-900 mt-1">
                {serviceOrderData?.description || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Required Field Force
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.requiredFieldforce || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Estimated Efforts
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.efforts || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-500">
                Downtime
              </label>
              <p className="text-base text-gray-900 mt-1">
                {jobOverview?.downtime || "N/A"}
              </p>
            </div>
          </div>
        ),
      },
      tasks: {
        label: "Tasks",
        icon: <CheckSquare className="h-4 w-4" />,
        count: tasksData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(tasksData || [])}
            columns={tasksColumns}
            rowKey="taskId"
            pagination={createPaginationConfig(tasksData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No tasks found"
          />
        ),
      },
      materials: {
        label: "Materials",
        icon: <Package className="h-4 w-4" />,
        count: materialsData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(materialsData || [])}
            columns={materialsColumns}
            rowKey="materialId"
            pagination={createPaginationConfig(materialsData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No materials found"
          />
        ),
      },
      readings: {
        label: "Readings",
        icon: <Wrench className="h-4 w-4" />,
        count: readingsData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(readingsData || [])}
            columns={readingsColumns}
            rowKey="readingId"
            pagination={createPaginationConfig(readingsData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No readings found"
          />
        ),
      },
      help: {
        label: "Help Resources",
        icon: <HelpCircle className="h-4 w-4" />,
        shortLabel: "Help",
        count: helpResourcesData?.length || 0,
        component: (
          <GenericTable
            data={getPaginatedData(helpResourcesData || [])}
            columns={helpResourcesColumns}
            rowKey="resourceId"
            pagination={createPaginationConfig(helpResourcesData || [])}
            onPageChange={setCurrentPage}
            emptyMessage="No help resources found"
          />
        ),
      },
    }),
    [
      currentPage,
      tasksData,
      materialsData,
      readingsData,
      helpResourcesData,
      jobOverview,
      itemsPerPage,
    ]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Assigned Service Order -{" "}
                {serviceOrderData?.soName || "Service Order"}
              </h1>
              <p className="text-gray-600 mt-2">
                Job Card: {serviceOrderData?.jobCardNo}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Badge className={getStatusColor(serviceOrderData?.status)}>
                {serviceOrderData?.status || "Unknown"}
              </Badge>
              <Button
                variant="outline"
                onClick={() => navigate(backNavigationPath)}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {backButtonText}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-l-4 border-l-green-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                SLA Status
              </CardTitle>
              <TrendingUp className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold ">{kpiData.slaStatus}</div>
              <p className="text-xs text-gray-500 mt-1">
                Within acceptable limits
              </p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                SLA Due Date
              </CardTitle>
              <Clock className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold ">{kpiData.slaDueDate}</div>
              <p className="text-xs text-gray-500 mt-1">Meeting timeline</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500 hover:shadow-lg transition-shadow ">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium text-gray-700">
                Time Remaining
              </CardTitle>
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{kpiData.timeRemaining}</div>
              <p className="text-xs text-gray-500 mt-1">On schedule</p>
            </CardContent>
          </Card>
        </div>

        {/* Service Order and Consumer/Meter Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Service Order Details Card */}
          <Card className="bg-white shadow-sm border border-gray-100 rounded-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-800">
                <Activity className="h-5 w-5 text-blue-600" />
                Service Order Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    SO Name
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.soName || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Job Card No
                  </label>
                  <p className="text-base text-gray-900 mt-1 font-mono">
                    {serviceOrderData?.jobCardNo || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Created On
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.createdOn || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Assigned On
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.assignedOn || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Expected Completion
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.expectedCompletion || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Utility Services
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.utilityServices || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Area
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.area || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Subarea
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.subarea || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Tax Rate
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.taxRate || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Premise
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.premise || "N/A"}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Description
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.description || "N/A"}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-medium text-gray-500">
                    Remark
                  </label>
                  <p className="text-base text-gray-900 mt-1">
                    {serviceOrderData?.remark || "N/A"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Consumer & Meter Details / Assigned Technician Card */}
          <Card className="bg-white shadow-sm border border-gray-100 rounded-lg">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
              <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-800">
                <Users className="h-5 w-5 text-green-600" />
                Consumer & Meter Details / Assigned Technician
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <Tabs
                defaultValue="consumer-meter"
                idPrefix="consumer-details"
                tabComponents={consumerMeterTabComponents}
                tabsListClassName="grid w-full grid-cols-2"
                disableUrlSync={true}
              />
            </CardContent>
          </Card>
        </div>

        {/* Job Requirements Card */}
        <Card className="bg-white shadow-sm border border-gray-100 rounded-lg">
          <CardHeader className="bg-gradient-to-r from-slate-50 to-gray-50 border-b p-2">
            <CardTitle className="flex items-center gap-3 text-xl font-semibold text-gray-800">
              <Package className="h-5 w-5" />
              Job Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <Tabs
              defaultValue="overview"
              idPrefix="job-requirements"
              tabComponents={jobTabComponents}
              tabsListClassName="grid w-full grid-cols-5"
              disableUrlSync={false}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AssignedConsumerServiceOrderView;
